import {assertDataPresent} from '@github-ui/assert-data-present'
import {useAnalytics, useClickAnalytics} from '@github-ui/use-analytics'
import {ActionList, Dialog, type ButtonProps} from '@primer/react'

import {useMergeMethodContext} from '../../../contexts/MergeMethodContext'
import {mergeButtonText} from '../../../helpers/merge-button-text'
import {
  MergeMethod,
  MergeAction,
  type ViewerMergeActions,
  type ViewerMergeMethods,
  type PullRequestMergeAction,
  type PullRequestMergeConditionResult,
} from '../../../types'
import {ConfirmMerge} from '../../ConfirmMerge'
import {ButtonWithDropdown} from '../common/ButtonWithDropdown'
import {MergeDropdownOption} from './MergeDropdownOption'
import type {PullRequestMergeRequirementsPayload} from '../../../page-data/payloads/merge-box'
import {Status} from '../../../helpers/mergeability-status'
import {MergeSectionActions} from './MergeSectionActions'
import {PreviewFeedbackMenu} from './PreviewFeedbackMenu'
import type {MergeBoxRollupStatus} from '../../../helpers/merge-box-status-calculator/types'
import {
  buildPullRequestUserActionContext,
  type PullRequestUserActionStack,
} from '@github-ui/pull-requests/analytics/pull-request-user-action'

/**
 * Return a list of merge methods to show in the "select merge method" dropdown
 */
export function allowableMergeMethods({mergeMethods}: {mergeMethods: ViewerMergeMethods}): MergeMethod[] {
  return mergeMethods.reduce<MergeMethod[]>((allowedMergeMethods, method) => {
    if (method?.allowableStatus === 'ALLOWED' || method?.allowableStatus === 'ALLOWED_WITH_BYPASS') {
      allowedMergeMethods.push(method.name)
    }

    return allowedMergeMethods
  }, [])
}

export type MergeButtonFocusProps = {
  shouldFocusPrimaryMergeButton: boolean
  setShouldFocusPrimaryMergeButton: (shouldFocus: boolean) => void
}

export type DirectMergeActionsSectionProps = {
  baseRefName: string
  defaultCommitAuthorEmail: PullRequestMergeRequirementsPayload['defaultCommitAuthorEmail']
  directMergeConfirmation?: 'inline' | 'dialog'
  commitMessageBody: PullRequestMergeRequirementsPayload['commitMessageBody']
  commitMessageHeadline: PullRequestMergeRequirementsPayload['commitMessageHeadline']
  conflictsCondition: {result: PullRequestMergeConditionResult} | undefined
  handleConfirmingMergeInfo: (isConfirming: boolean) => void
  isAdminBypassToggleChecked: boolean
  isAdminBypassToggleVisible: boolean
  isAutoMergeAllowed?: boolean
  isConfirmingMergeInfo: boolean
  isStacked?: boolean
  mergeCurrentlyBlockedForStacks?: boolean
  onStackMergePendingChange?: (pendingAction?: PullRequestMergeAction) => void
  stackedEntriesToMergeCount?: number
  stackMetadata?: PullRequestUserActionStack
  currentStackEntryPositionNumber?: number
  stackSize?: number
  mergeable: boolean
  numberOfCommits: number
  mergeBoxRollupStatus: MergeBoxRollupStatus
  status: Status
  viewerMergeActions: ViewerMergeActions
  possibleCommitAuthorEmails: PullRequestMergeRequirementsPayload['possibleCommitAuthorEmails']
  buttonSize?: ButtonProps['size']
  instructions?: React.ReactNode
} & MergeButtonFocusProps

/**
 * Renders the merge button and provides the ability to select from the available merge methods
 */
export function DirectMergeActionsSection({
  baseRefName,
  buttonSize,
  defaultCommitAuthorEmail,
  directMergeConfirmation = 'inline',
  commitMessageBody,
  commitMessageHeadline,
  conflictsCondition,
  handleConfirmingMergeInfo,
  isAdminBypassToggleChecked,
  isAdminBypassToggleVisible,
  isAutoMergeAllowed = false,
  isConfirmingMergeInfo,
  isStacked = false,
  mergeCurrentlyBlockedForStacks = false,
  onStackMergePendingChange,
  stackedEntriesToMergeCount = 1,
  stackMetadata,
  currentStackEntryPositionNumber,
  stackSize,
  mergeable,
  numberOfCommits,
  mergeBoxRollupStatus,
  setShouldFocusPrimaryMergeButton,
  shouldFocusPrimaryMergeButton,
  status,
  viewerMergeActions,
  possibleCommitAuthorEmails,
  instructions,
}: DirectMergeActionsSectionProps) {
  const {mergeMethod, setMergeMethod} = useMergeMethodContext()
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const {sendAnalyticsEvent} = useAnalytics()

  if (!conflictsCondition) return null
  const directMergeAction = viewerMergeActions.find(action => action.name === MergeAction.DIRECT_MERGE)
  assertDataPresent(directMergeAction)

  const allowedMergeMethods = allowableMergeMethods({mergeMethods: directMergeAction.mergeMethods})
  const canCreateMergeCommit = allowedMergeMethods.includes(MergeMethod.MERGE)
  const canSquashMerge = allowedMergeMethods.includes(MergeMethod.SQUASH)
  const canRebaseMerge = allowedMergeMethods.includes(MergeMethod.REBASE)

  // account for a scenario where the button is checked, then a live update causes the PR's state to update to
  // be mergeable and we stop showing the checkbox
  const isBypassRulesActive = isAdminBypassToggleVisible && isAdminBypassToggleChecked

  const inactiveTooltipTextVerb = status === Status.ChecksPending ? 'pending' : 'failing'
  const inactiveTooltipText = mergeCurrentlyBlockedForStacks
    ? 'Merging is blocked due to failing stack merge requirements'
    : `Merging is blocked due to ${inactiveTooltipTextVerb} merge requirements`
  const shouldSendClickAnalyticsEvent = isStacked && stackMetadata && currentStackEntryPositionNumber && stackSize

  const confirmMerge = () => {
    handleConfirmingMergeInfo(true)

    if (shouldSendClickAnalyticsEvent) {
      sendClickAnalyticsEvent(
        buildPullRequestUserActionContext({
          category: 'merge_box',
          action: 'stack_merge_click',
          data: {
            stack: stackMetadata,
            merge_method: mergeMethod,
            stack_size: stackSize,
            position_in_stack: currentStackEntryPositionNumber,
            uses_merge_queue: false,
          },
        }),
      )
    }

    const analyticsEventKey = isAutoMergeAllowed
      ? 'direct_merge_section.auto_merge_click'
      : 'direct_merge_section.direct_merge_click'

    const analyticsEventValue = isAutoMergeAllowed
      ? 'MERGEBOX_AUTO_MERGE_SECTION_MERGE_BUTTON'
      : 'MERGEBOX_DIRECT_MERGE_SECTION_MERGE_BUTTON'

    sendAnalyticsEvent(analyticsEventKey, analyticsEventValue)
  }

  const cancelMerge = () => {
    handleConfirmingMergeInfo(false)
    setShouldFocusPrimaryMergeButton(true)

    const analyticsEventKey = isAutoMergeAllowed
      ? 'direct_merge_section.cancel_auto_merge'
      : 'direct_merge_section.cancel_direct_merge'

    const analyticsEventValue = isAutoMergeAllowed
      ? 'MERGEBOX_AUTO_MERGE_CANCEL_CONFIRMATION_BUTTON'
      : 'MERGEBOX_DIRECT_MERGE_CANCEL_CONFIRMATION_BUTTON'

    sendAnalyticsEvent(analyticsEventKey, analyticsEventValue)
  }

  const updateMergeMethod = (newMergeMethod: MergeMethod, analyticsEvent: string) => {
    setMergeMethod(newMergeMethod)
    sendAnalyticsEvent(analyticsEvent, 'MERGEBOX_DIRECT_MERGE_SECTION_MERGE_METHOD_MENU_ITEM')
  }

  if (isConfirmingMergeInfo) {
    const confirmation = (
      <ConfirmMerge
        buttonSize={directMergeConfirmation === 'dialog' ? undefined : buttonSize}
        defaultCommitAuthorEmail={defaultCommitAuthorEmail || ''}
        commitMessageBody={commitMessageBody || ''}
        commitMessageHeadline={commitMessageHeadline || ''}
        defaultBranchName={baseRefName}
        handleConfirmingMergeInfo={handleConfirmingMergeInfo}
        isBypassMerge={isAdminBypassToggleChecked}
        isStacked={isStacked}
        onStackMergePendingChange={onStackMergePendingChange}
        stackedEntriesToMergeCount={stackedEntriesToMergeCount}
        selectedMergeMethod={mergeMethod}
        onCancel={cancelMerge}
        isAutoMergeAllowed={isAutoMergeAllowed}
        possibleCommitAuthorEmails={possibleCommitAuthorEmails}
      />
    )

    return directMergeConfirmation === 'dialog' ? (
      <Dialog title="Confirm merge" onClose={cancelMerge}>
        {confirmation}
      </Dialog>
    ) : (
      confirmation
    )
  }

  const isMergingMultiplePullRequests = isStacked && stackedEntriesToMergeCount > 1
  const stackedPRsButtonCount = isMergingMultiplePullRequests ? stackedEntriesToMergeCount : undefined

  let mergeOptionText
  if (isMergingMultiplePullRequests) {
    mergeOptionText = `All commits from these ${stackedEntriesToMergeCount} branches will be added to the base branch via merge commits.`
  } else {
    mergeOptionText = `All commits from this branch will be added to the base branch via a merge commit.`
  }

  let squashOptionText
  if (isMergingMultiplePullRequests) {
    squashOptionText = `The commits from these ${stackedEntriesToMergeCount} branches will be combined into one commit per branch in the base branch.`
  } else if (numberOfCommits === 1) {
    squashOptionText = 'The 1 commit from this branch will be added to the base branch.'
  } else {
    squashOptionText = `The ${numberOfCommits} commits from this branch will be combined into one commit in the base branch.`
  }

  let rebaseOptionText
  if (isMergingMultiplePullRequests) {
    rebaseOptionText = `All commits from these ${stackedEntriesToMergeCount} branches will be rebased and added to the base branch.`
  } else if (numberOfCommits === 1) {
    rebaseOptionText = 'The 1 commit from this branch will be rebased and added to the base branch.'
  } else {
    rebaseOptionText = `The ${numberOfCommits} commits from this branch will be rebased and added to the base branch.`
  }

  return (
    <>
      <MergeSectionActions>
        <MergeSectionActions.Slot>
          <ButtonWithDropdown
            size={buttonSize}
            count={stackedPRsButtonCount}
            hideSecondaryButton={allowedMergeMethods.length === 1}
            inactive={
              (mergeCurrentlyBlockedForStacks && !isBypassRulesActive) ||
              (isAutoMergeAllowed ? false : !mergeable && !isBypassRulesActive)
            }
            inactiveTooltipText={inactiveTooltipText}
            inactiveTooltipDirection="se"
            isPrimary={mergeable && mergeBoxRollupStatus === 'ALL_PASSED'}
            secondaryButtonActive
            secondaryButtonAriaLabel="Select merge method"
            shouldFocusPrimaryButton={shouldFocusPrimaryMergeButton}
            actionList={
              <ActionList selectionVariant="single" showDividers>
                {canCreateMergeCommit && (
                  <MergeDropdownOption
                    primaryText="Create a merge commit"
                    secondaryText={mergeOptionText}
                    selected={mergeMethod === MergeMethod.MERGE}
                    onSelect={() =>
                      updateMergeMethod(MergeMethod.MERGE, 'direct_merge_section.select_create_a_merge_commit')
                    }
                  />
                )}
                {canSquashMerge && (
                  <MergeDropdownOption
                    primaryText="Squash and merge"
                    secondaryText={squashOptionText}
                    selected={mergeMethod === MergeMethod.SQUASH}
                    onSelect={() =>
                      updateMergeMethod(MergeMethod.SQUASH, 'direct_merge_section.select_squash_and_merge')
                    }
                  />
                )}
                {canRebaseMerge && (
                  <MergeDropdownOption
                    primaryText="Rebase and merge"
                    secondaryText={rebaseOptionText}
                    selected={mergeMethod === MergeMethod.REBASE}
                    onSelect={() =>
                      updateMergeMethod(MergeMethod.REBASE, 'direct_merge_section.select_rebase_and_merge')
                    }
                  />
                )}
              </ActionList>
            }
            onFocusPrimaryButton={() => setShouldFocusPrimaryMergeButton(false)}
            onPrimaryButtonClick={confirmMerge}
          >
            {mergeButtonText({
              mergeMethod,
              confirming: false,
              isBypassMerge: isBypassRulesActive,
              isAutoMergeAllowed,
              isMergingMultiple: isMergingMultiplePullRequests,
            })}
          </ButtonWithDropdown>
        </MergeSectionActions.Slot>
        {!!instructions && <MergeSectionActions.Slot>{instructions}</MergeSectionActions.Slot>}
        {isStacked && (
          <MergeSectionActions.Slot>
            <PreviewFeedbackMenu />
          </MergeSectionActions.Slot>
        )}
      </MergeSectionActions>
    </>
  )
}
